'use client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { GetTokenDialog, useViduToken } from '@/app/components/shared/UserCenter';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { FAQ, type FAQItem } from '@/app/components/ui/FAQ';
import { VIDU_SKILL_GITHUB_URL } from '@/app/constants';
import { useClaw, useCopy } from '@/app/hooks';
import { useRouter } from '@/i18n/routing';
import { DiscoverMoreSkills } from './DiscoverMoreSkills';

const HERO_BG = 'https://image01.cf.vidu.studio/vidu/media-asset/agent-2b8da11d.webp';
const CTA_BG = 'https://image01-aliyun.vidu.zone/vidu/media-asset/claw-product-img-1-09d87dba.webp';

const SAMPLE_PROMPT = 'Use Vidu to generate a video of a cyberpunk race car speeding through a neon city.';
const ENV_SNIPPET =
  'export VIDU_TOKEN=your_token\nexport VIDU_BASE_URL=https://service.vidu.com # for regions outside the Chinese mainland';

// 营销方式（标题/描述来自 i18n，媒体资源固定）
const MARKETING_METHOD_MEDIA = [
  {
    key: 'zeroSetup',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/video-1-img-47522753-a2676f45.webp',
    videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/video-1-0c3a5b4e-2f3f1cfb.mp4',
    mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/video-1-0c3a5b4e-7e948a1a.mp4',
  },
  {
    key: 'automation',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/video-2-img-9a70d00b.webp',
    videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/video-2-71b36385.mp4',
    mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/video-2-71b36385-1677d35d.mp4',
  },
  {
    key: 'skills',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/video-3-img-98221440.webp',
    videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/video-3-e25919c7.mp4',
    mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/video-3-e25919c7-3ca3c1f1.mp4',
  },
] as const;

// 适用场景
const SCENARIO_MEDIA = [
  { key: 'social', image: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-claw-product-img-1-9e3281da.webp' },
  { key: 'ecommerce', image: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-claw-product-img-2-0e08b7e6.webp' },
  { key: 'growth', image: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-claw-product-img-3-052cf8ce.webp' },
  {
    key: 'smallBusiness',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-claw-product-img-4-a289b0fc.webp',
  },
] as const;

// 可定制技能
const SKILL_MEDIA = [
  { key: 'socialAutomation', img: 'https://image01.cf.vidu.studio/vidu/media-asset/scenarios-img-1-c7123497.webp' },
  { key: 'scriptToVideo', img: 'https://image01.cf.vidu.studio/vidu/media-asset/scenarios-img-2-762c3a16.webp' },
  { key: 'aiAvatar', img: 'https://image01.cf.vidu.studio/vidu/media-asset/scenarios-img-3-219763cc.webp' },
  { key: 'textToVideo', img: 'https://image01.cf.vidu.studio/vidu/media-asset/scenarios-img-4-3c961f7c.webp' },
  { key: 'faceless', img: 'https://image01.cf.vidu.studio/vidu/media-asset/scenarios-img-5-92459bce.webp' },
] as const;

const FAQ_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'] as const;

export function ViduClawGlobal() {
  const t = useTranslations('viduClaw');
  const tSection = useTranslations('relatedProducts');
  const [activeMarketingMethod, setActiveMarketingMethod] = useState<number>(0);
  const { copy } = useCopy();
  const router = useRouter();

  const { getMyClaws } = useClaw();

  const {
    open: openTokenDialog,
    setOpen: setOpenTokenDialog,
    token: viduToken,
    requestToken,
    handleReset,
  } = useViduToken({
    onResetTokenSuccess: () => {
      getMyClaws();
    },
  });

  const openClaw = () => {
    router.push('/claw');
  };

  const skillsScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateSkillsScrollState = () => {
    const el = skillsScrollRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 1);
    setCanScrollNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  useEffect(() => {
    const el = skillsScrollRef.current;
    if (!el) return;
    updateSkillsScrollState();
    el.addEventListener('scroll', updateSkillsScrollState, { passive: true });
    window.addEventListener('resize', updateSkillsScrollState);
    return () => {
      el.removeEventListener('scroll', updateSkillsScrollState);
      window.removeEventListener('resize', updateSkillsScrollState);
    };
  }, []);

  const scrollSkills = (direction: 'prev' | 'next') => {
    const el = skillsScrollRef.current;
    if (!el) return;
    // 单卡宽度 500 + gap 24
    const amount = 524;
    el.scrollBy({ left: direction === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  const marketingMethods = MARKETING_METHOD_MEDIA.map((media) => ({
    ...media,
    title: t(`marketingMethods.${media.key}.title`),
    description: t(`marketingMethods.${media.key}.description`),
  }));

  const scenarios = SCENARIO_MEDIA.map((media) => ({
    ...media,
    title: t(`scenarios.${media.key}.title`),
    description: t(`scenarios.${media.key}.description`),
  }));

  const skills = SKILL_MEDIA.map((media) => ({
    ...media,
    title: t(`skills.${media.key}.title`),
    description: t(`skills.${media.key}.description`),
  }));

  const faqItems: FAQItem[] = FAQ_KEYS.map((key, index) => ({
    id: `faq-${index + 1}`,
    question: t(`faq.${key}.question`),
    answer: [{ title: t(`faq.${key}.answer`) }],
  }));

  return (
    <div className="w-full bg-black pb-10 text-white">
      <section className="relative mx-auto h-[624px] max-w-[1920px] overflow-hidden">
        <img
          src={HERO_BG}
          alt="Vidu Agent AI Marketing Agent"
          decoding="async"
          fetchPriority="high"
          className="pointer-events-none absolute inset-0 size-full select-none object-cover object-top"
        />
      </section>
      <section className="-mt-24">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center px-6 text-center sm:px-4">
          <p className="mt-4 text-system-white80 text-xl sm:text-base">{t('hero.tagline')}</p>
          <p className="text-center text-system-white80 text-xl sm:text-sm">{t('hero.subtitle')}</p>
          <div className="mt-8 flex items-center gap-4 sm:w-full sm:flex-col">
            <div
              onClick={() => {
                router.push('/vidu-claw/showcase');
              }}
              className="inline-flex h-12 w-[220px] cursor-pointer items-center justify-center rounded-[100px] border border-system-white12 bg-system-white32 font-medium text-white hover:bg-system-hover01"
            >
              {t('hero.viewShowcase')}
            </div>
            <a
              href="#skills"
              className="inline-flex h-12 w-[220px] items-center justify-center rounded-[100px] border border-system-white12 bg-system-white32 font-medium text-white hover:bg-system-hover01"
            >
              {t('hero.useSkills')}
            </a>
            <div
              className="inline-flex h-12 w-[220px] cursor-pointer items-center justify-center rounded-[100px] bg-[#0086FF] font-medium text-white hover:bg-[#0d92ff]"
              onClick={() => {
                openClaw();
              }}
            >
              {t('hero.tryClaw')}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1200px] px-6 pt-28 text-center sm:px-4">
        <h1 className="font-semibold text-4xl">{t('intro.title')}</h1>
        <p className="mt-2 text-system-white64 leading-7">{t('intro.subtitle')}</p>
      </section>

      <section className="mx-auto mt-12 flex h-[320px] w-full max-w-[1200px] items-center justify-center gap-6">
        <div className="relative h-full w-[3px]">
          <div className="h-full w-[1px] bg-system-white48" />
          <div
            className="-left-[1px] absolute top-0 h-[160px] w-[3px] rounded-full bg-system-white"
            style={{
              transform: `translateY(${activeMarketingMethod * 80}px)`,
            }}
          />
        </div>

        <div className="flex flex-1 flex-col gap-10">
          {marketingMethods.map((item, index) => {
            const isActive = index === activeMarketingMethod;
            if (isActive) {
              return (
                <div key={item.key} className="flex flex-col gap-6">
                  <h3 className="font-semibold text-[32px] leading-[32px]">{item.title}</h3>
                  <div className="text-base text-system-white36">{item.description}</div>
                </div>
              );
            }
            return (
              <h3
                key={item.key}
                className="cursor-pointer font-semibold text-[32px] text-system-white64 leading-[1.6]"
                onClick={() => {
                  setActiveMarketingMethod(index);
                }}
              >
                {item.title}
              </h3>
            );
          })}
        </div>

        <div className="ml-10 h-[320px] w-[576px] overflow-hidden rounded-[20px]">
          {marketingMethods.map((item, index) => (
            <VideoLazyLoad
              key={item.videoSrc}
              videoSrc={item.videoSrc}
              mobileVideoSrc={item.mobileVideoSrc}
              poster={item.image}
              className={`${activeMarketingMethod === index ? 'block' : 'hidden'} h-full w-full`}
              alt={item.title}
            />
          ))}
        </div>
      </section>

      <section className="mx-auto mt-[160px] w-full max-w-[1200px] px-6 text-center sm:px-4">
        <h2 className="font-semibold text-[40px] leading-[48px] sm:text-3xl">{t('scenariosTitle')}</h2>
      </section>

      <section className="mx-auto mt-12 grid w-full max-w-[1200px] grid-cols-2 gap-8 px-6 sm:grid-cols-1 sm:px-4">
        {scenarios.map((item) => (
          <article key={item.key} className="overflow-hidden rounded-[20px]">
            <img
              src={item.image}
              loading="lazy"
              decoding="async"
              alt={item.title}
              className="aspect-video w-full rounded-[20px] object-cover"
            />
            <div className="px-6 py-4">
              <h3 className="font-semibold text-2xl sm:text-xl">{item.title}</h3>
              <p className="mt-2 whitespace-pre-line text-sm text-system-white64 leading-6">{item.description}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="mt-12 flex w-full justify-center px-6 sm:px-4">
        <a
          href="https://www.vidu.com/claw"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-12 w-[220px] items-center justify-center rounded-[100px] bg-[#0086FF] font-medium text-white hover:bg-[#0d92ff]"
        >
          {t('finalCta.tryClaw')}
        </a>
      </section>

      <section className="mt-[160px] w-full px-6 sm:px-4">
        <h2 className="mx-auto mb-4 max-w-[1200px] text-center font-semibold text-4xl sm:text-[28px]">
          {t('customize.title')}
        </h2>
        <div className="mx-auto max-w-[1200px] text-center text-base text-system-white64">
          {t('customize.description')}
        </div>
        <div className="group relative mx-auto mt-8 max-w-[1200px]">
          <div
            ref={skillsScrollRef}
            className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] sm:grid sm:grid-cols-1 [&::-webkit-scrollbar]:hidden"
          >
            {skills.map((item) => {
              return (
                <div className="w-[500px] flex-shrink-0 snap-start rounded-16 bg-system-white16 p-10" key={item.key}>
                  <img
                    src={item.img}
                    loading="lazy"
                    decoding="async"
                    alt={item.title}
                    className="mb-8 h-[52px] object-cover"
                  />
                  <h3 className="mb-4 font-semibold text-2xl">{item.title}</h3>
                  <p className="mt-3 text-system-white64">{item.description}</p>
                </div>
              );
            })}
          </div>

          {/* hover 浮现的左右滑动按钮（仅桌面端） */}
          <button
            type="button"
            aria-label={tSection('prev')}
            disabled={!canScrollPrev}
            onClick={() => scrollSkills('prev')}
            className="-translate-y-1/2 -translate-x-1/2 absolute top-1/2 left-0 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white text-xl opacity-0 backdrop-blur-sm transition-opacity duration-300 hover:bg-white/10 disabled:cursor-not-allowed group-hover:opacity-100 sm:hidden"
          >
            <BaseLineLeft className={canScrollPrev ? '' : 'opacity-30'} />
          </button>
          <button
            type="button"
            aria-label={tSection('next')}
            disabled={!canScrollNext}
            onClick={() => scrollSkills('next')}
            className="-translate-y-1/2 absolute top-1/2 right-0 flex h-12 w-12 translate-x-1/2 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white text-xl opacity-0 backdrop-blur-sm transition-opacity duration-300 hover:bg-white/10 disabled:cursor-not-allowed group-hover:opacity-100 sm:hidden"
          >
            <BaseLineRight className={canScrollNext ? '' : 'opacity-30'} />
          </button>
        </div>
      </section>

      <section id="skills" className="mx-auto mt-[160px] w-full max-w-[1200px] px-6 sm:px-4">
        <h2 className="mb-4 text-center font-semibold text-4xl sm:text-[28px]">{t('steps.title')}</h2>
        <div className="text-center text-base text-system-white64">{t('steps.subtitle')}</div>
        <div className="mt-8 rounded-16 rounded-24 bg-white/[0.08] p-10 sm:p-5">
          <div className="space-y-10">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xl sm:text-lg">
                <span className="font-bold text-[#0086FF]">1.</span>
                <h3 className="font-medium text-white">{t('steps.step1')}</h3>
              </div>
              <div className="relative">
                <pre className="overflow-x-auto rounded-16 border border-system-white12 bg-system-black36 px-6 py-5 text-sm text-system-white64 sm:px-4 sm:py-4">
                  {t('steps.installCmd')} {VIDU_SKILL_GITHUB_URL}
                </pre>
                <button
                  type="button"
                  aria-label="Copy install command"
                  className="-translate-y-1/2 absolute top-1/2 right-5 sm:right-3"
                  onClick={() => {
                    copy(`${t('steps.installCmd')} ${VIDU_SKILL_GITHUB_URL}`);
                  }}
                >
                  <BaseLineCopy className="size-4 text-system-white" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-xl sm:text-lg">
                  <span className="font-bold text-[#0086FF]">2.</span>
                  <h3 className="font-medium text-white">{t('steps.step2')}</h3>
                </div>
                <div
                  className="cursor-pointer rounded-16 border border-[rgba(0,134,255,0.40)] bg-[rgba(0,134,255,0.24)] px-4 py-1.5 text-sm text-white"
                  onClick={requestToken}
                >
                  {t('steps.getToken')}
                </div>
              </div>
              <div className="relative">
                <pre className="overflow-x-auto rounded-16 border border-system-white12 bg-system-black36 px-6 py-5 text-sm text-system-white64 sm:px-4 sm:py-4">
                  {ENV_SNIPPET}
                </pre>
                <button
                  type="button"
                  aria-label="Copy environment variables"
                  className="absolute top-6 right-5 sm:top-4 sm:right-3"
                  onClick={() => {
                    copy(ENV_SNIPPET);
                  }}
                >
                  <BaseLineCopy className="size-4 text-system-white" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xl sm:text-lg">
                  <span className="font-bold text-[#0086FF]">3.</span>
                  <h3 className="font-medium text-white">{t('steps.step3')}</h3>
                </div>
                <p className="pl-6 text-base text-system-white64 sm:pl-0 sm:text-sm">{t('steps.step3desc')}</p>
              </div>
              <div className="relative">
                <pre className="overflow-x-auto rounded-16 border border-system-white12 bg-system-black36 px-6 py-5 text-sm text-system-white64 sm:px-4 sm:py-4">
                  {SAMPLE_PROMPT}
                </pre>
                <button
                  type="button"
                  aria-label="Copy sample prompt"
                  className="-translate-y-1/2 absolute top-1/2 right-5 sm:right-3"
                  onClick={() => {
                    copy(SAMPLE_PROMPT);
                  }}
                >
                  <BaseLineCopy className="size-4 text-system-white" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DiscoverMoreSkills />

      <section className="mx-auto mt-[160px] w-full max-w-[1200px] px-6 sm:px-4">
        <FAQ
          items={faqItems}
          className="max-w-[1200px]"
          classNameContainer="justify-between"
          customTopMargin="!mt-8"
          customBottomMargin="!mb-0"
          classNameTitle="!w-full"
          classNameList="mr-0"
          classNameTitleContainer="ml-0"
        />
      </section>

      <section className="relative mt-16 overflow-hidden py-20 sm:py-14">
        <img
          src={CTA_BG}
          alt="Vidu Agent call to action background"
          decoding="async"
          fetchPriority="low"
          loading="lazy"
          className="pointer-events-none absolute inset-0 size-full select-none object-cover object-center"
        />
        <div className="relative mx-auto flex w-full max-w-[1200px] flex-col items-center px-6 text-center sm:px-4">
          <h2 className="font-semibold text-5xl sm:text-3xl">
            <img
              src="https://image01-aliyun.vidu.zone/vidu/media-asset/welcome-img-ac1939ed.webp"
              loading="lazy"
              decoding="async"
              alt="Vidu Agent"
              className="h-20 w-full object-cover"
            />
          </h2>
          <p className="text-system-white80">{t('hero.tagline')}</p>
          <div className="mt-6 flex gap-4 sm:w-full sm:flex-col">
            <a
              href="#skills"
              className="inline-flex h-12 w-[220px] items-center justify-center rounded-[100px] border border-system-white12 bg-system-white32 font-medium text-white hover:bg-system-hover01"
            >
              {t('hero.useSkills')}
            </a>
            <div
              className="inline-flex h-12 w-[220px] cursor-pointer items-center justify-center rounded-[100px] bg-[#0086FF] font-medium text-white hover:bg-[#0d92ff]"
              onClick={() => {
                openClaw();
              }}
            >
              {t('finalCta.tryClaw')}
            </div>
          </div>
        </div>
      </section>
      <GetTokenDialog
        open={openTokenDialog}
        onOpenChange={setOpenTokenDialog}
        token={viduToken}
        onReset={handleReset}
      />
    </div>
  );
}
